import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import NewOrderDialog from '@/pages/NewOrderDialog';

const STATUS_LABELS = {
  draft: 'Draft', pending_approval: 'Pending approval',
  changes_requested: 'Changes requested', approved: 'Approved',
  submitted: 'Submitted', fulfilled: 'Fulfilled',
  rejected: 'Rejected', cancelled: 'Cancelled',
};
const FILTERS = ['all', 'draft', 'pending_approval', 'changes_requested', 'approved', 'submitted', 'fulfilled', 'rejected', 'cancelled'];

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/** Admin Orders tab — list, filter by status, launch new-order dialog. */
export default function AdminOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await api.get('/admin/orders', { params });
      setOrders(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="admin-orders-panel">
      <div className="flex flex-col gap-3">
        <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-visible -mx-1 px-1 no-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`sans text-[10px] tracking-widest uppercase px-3 py-1.5 border transition-colors whitespace-nowrap shrink-0 ${filter === f ? 'border-black bg-neutral-900 text-white' : 'border-neutral-200 text-neutral-500 hover:border-neutral-500'}`}
              data-testid={`admin-orders-filter-${f}`}
            >
              {f === 'all' ? 'All' : STATUS_LABELS[f]}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setDialogOpen(true)} className="btn-primary w-full md:w-auto md:self-end" data-testid="admin-orders-new">
          <Plus size={14} /> New order
        </button>
      </div>

      {loading ? (
        <div className="sans text-xs tracking-widest uppercase text-neutral-500 py-8 text-center flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">
          No orders in this view yet.
        </div>
      ) : (
        <>
        {/* Mobile card list */}
        <div className="md:hidden space-y-3">
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              className="block border border-neutral-200 p-4 active:bg-neutral-50 transition-colors"
              data-testid={`admin-orders-row-${o.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="serif text-base leading-tight">{o.order_number}</p>
                  <p className="sans text-[11px] text-neutral-500 mt-0.5">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`badge badge--${o.status} shrink-0`}>{STATUS_LABELS[o.status] || o.status}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <p className="sans text-sm text-neutral-900 truncate">{o.business_name || o.user_email}</p>
                {o.business_name && (
                  <p className="sans text-[11px] text-neutral-500 truncate">{o.user_email}</p>
                )}
              </div>
              <div className="mt-3 flex items-end justify-between gap-3">
                <span className="sans text-[10px] tracking-widest uppercase text-neutral-500">
                  {(o.items || []).length} {(o.items || []).length === 1 ? 'item' : 'items'}
                </span>
                <p className="serif text-lg leading-none">{fmt(o.total)}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-right px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="px-4 py-3">
                    <Link to={`/orders/${o.id}`} className="serif hover:underline">{o.order_number}</Link>
                    <p className="sans text-[11px] text-neutral-500 mt-0.5">{new Date(o.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3 sans">
                    <p>{o.business_name || o.user_email}</p>
                    <p className="text-[11px] text-neutral-500">{o.user_email}</p>
                  </td>
                  <td className="px-4 py-3 sans text-right">{(o.items || []).length}</td>
                  <td className="px-4 py-3 serif text-right">{fmt(o.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`badge badge--${o.status}`}>{STATUS_LABELS[o.status] || o.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <NewOrderDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={load} />
    </div>
  );
}
